<div class="absolute inset-0 flex flex-col overflow-hidden">
  <!-- Header -->
  <div
    class="bg-default dark:bg-transparent p-6 sm:p-10 sm:pt-12 sm:pb-28 flex flex-col sm:flex-row sm:justify-between sm:items-center"
  >
    <div class="flex-1 min-w-0">
      <div class="flex items-center mb-2">
        <a
          class="inline-flex items-center text-secondary font-medium cursor-pointer hover:opacity-80 transition-opacity"
          (click)="goBack()"
        >
          <mat-icon
            class="icon-size-5 text-secondary"
            [svgIcon]="'heroicons_solid:chevron-left'"
          ></mat-icon>
          <span class="ml-1">Back</span>
        </a>
      </div>
      <h2
        class="text-3xl md:text-4xl font-extrabold leading-tight truncate text-gray-900 dark:text-white"
      >
        {{ isEditMode ? "Edit Organization" : "Organization Profile" }}
      </h2>
    </div>

    <div class="flex shrink-0 items-center mt-6 sm:mt-0 sm:ml-4">
      <!-- Only show Edit button for admin users -->
      <button
        *ngIf="!isEditMode && isAdminLoggedIn"
        class="px-8 py-2"
        mat-raised-button
        color="primary"
        (click)="toggleEditMode()"
      >
        Edit Profile
      </button>
    </div>
  </div>

  <!-- Content -->
  <div
    class="flex-auto -mt-16 bg-white dark:bg-gray-900 p-6 sm:p-8 rounded-t-2xl shadow-lg overflow-y-auto"
    cdkScrollable
  >
    <!-- Edit Mode -->
    <form
      *ngIf="isEditMode"
      class="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 sm:p-8 space-y-8 border border-gray-200 dark:border-gray-700"
      [formGroup]="orgForm"
      (ngSubmit)="onFormSubmit()"
    >
      <section class="space-y-6">
        <h3
          class="text-2xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-4"
        >
          Organization Details
        </h3>

        <!-- Logo Upload Section -->
        <div
          class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600"
        >
          <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Organization Logo
          </h4>

          <div
            class="flex flex-col lg:flex-row items-start lg:items-center gap-6"
          >
            <!-- Image Preview -->
            <div class="flex-shrink-0">
              <div *ngIf="imagePreviewOrg" class="relative">
                <img
                  [src]="imagePreviewOrg"
                  alt="Organization Image"
                  class="w-32 h-32 rounded-xl object-cover border-2 border-gray-300 dark:border-gray-600 shadow-md"
                />
              </div>
              <div
                *ngIf="!imagePreviewOrg"
                class="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-100 dark:bg-gray-700"
              >
                <span
                  class="text-gray-500 dark:text-gray-400 text-sm text-center px-2"
                  >No image</span
                >
              </div>
            </div>

            <!-- Upload Controls -->
            <div class="flex-1 space-y-3">
              <div class="flex flex-col sm:flex-row gap-3">
                <input
                  type="file"
                  #fileInput
                  accept="image/*"
                  (change)="onFileSelected($event)"
                  [disabled]="pageLoader"
                  class="hidden"
                />
                <button
                  type="button"
                  mat-stroked-button
                  color="primary"
                  (click)="fileInput.click()"
                  [disabled]="pageLoader"
                  class="flex items-center gap-2"
                >
                  <mat-icon>cloud_upload</mat-icon>
                  Choose Image
                </button>

                <button
                  *ngIf="imagePreviewOrg"
                  type="button"
                  mat-stroked-button
                  color="warn"
                  (click)="removeImage()"
                  [disabled]="pageLoader"
                  class="flex items-center gap-2"
                >
                  <mat-icon>delete</mat-icon>
                  Remove
                </button>
              </div>

              <!-- Upload Progress -->
              <div *ngIf="isUploading" class="w-full max-w-md">
                <mat-progress-bar
                  mode="determinate"
                  [value]="uploadPercent"
                  color="primary"
                ></mat-progress-bar>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Uploading: {{ uploadPercent }}%
                </p>
              </div>

              <p class="text-sm text-gray-600 dark:text-gray-400">
                Supported formats: JPG, PNG, GIF. Max size: 5MB
              </p>
            </div>
          </div>
        </div>

        <!-- Form Fields -->
        <div class="grid gap-6 sm:grid-cols-2">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label class="text-gray-700 dark:text-gray-300"
              >Organization Name</mat-label
            >
            <input
              matInput
              formControlName="organizationName"
              readonly
              class="text-gray-900 dark:text-white"
            />
            <mat-icon matSuffix class="text-gray-400">business</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label class="text-gray-700 dark:text-gray-300"
              >Phone Number</mat-label
            >
            <input
              matInput
              formControlName="phone"
              required
              class="text-gray-900 dark:text-white"
            />
            <mat-icon matSuffix class="text-gray-400">phone</mat-icon>
            <mat-error *ngIf="orgForm.get('phone')?.hasError('required')">
              Phone number is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full sm:col-span-2">
            <mat-label class="text-gray-700 dark:text-gray-300"
              >Organization Address</mat-label
            >
            <textarea
              matInput
              formControlName="address"
              rows="3"
              class="text-gray-900 dark:text-white resize-none"
              placeholder="Enter complete organization address"
            ></textarea>
            <mat-icon matSuffix class="text-gray-400">location_on</mat-icon>
          </mat-form-field>
        </div>
      </section>

      <!-- Action Buttons -->
      <div
        class="pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end gap-3"
      >
        <button
          type="button"
          (click)="toggleEditMode()"
          mat-stroked-button
          color="basic"
          class="px-6 py-2"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="orgForm.invalid || pageLoader"
          mat-raised-button
          color="primary"
          class="px-8 py-2"
        >
          <div class="flex items-center gap-2">
            <mat-icon *ngIf="!pageLoader">save</mat-icon>
            <mat-spinner *ngIf="pageLoader" diameter="20"></mat-spinner>
            <span>{{ pageLoader ? "Updating..." : "Save Changes" }}</span>
          </div>
        </button>
      </div>
    </form>

    <!-- View Mode -->
    <div
      *ngIf="!isEditMode"
      class="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700"
    >
      <h3
        class="text-2xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-4 mb-6"
      >
        Organization Details
      </h3>

      <!-- Organization Image -->
      <div
        class="flex flex-col lg:flex-row items-start lg:items-center gap-8 mb-8"
      >
        <div class="flex-shrink-0">
          <div *ngIf="imagePreviewOrg" class="relative">
            <img
              [src]="imagePreviewOrg"
              alt="Organization Image"
              class="w-40 h-40 rounded-2xl object-cover border-2 border-gray-300 dark:border-gray-600 shadow-lg"
            />
          </div>
          <div
            *ngIf="!imagePreviewOrg"
            class="w-40 h-40 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-100 dark:bg-gray-700"
          >
            <div class="text-center">
              <mat-icon class="text-gray-400 mb-2">photo_camera</mat-icon>
              <span class="text-gray-500 dark:text-gray-400 text-sm block"
                >No logo</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Details Grid -->
      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          class="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center gap-3 mb-2">
            <mat-icon class="text-gray-500 dark:text-gray-400 text-lg"
              >fingerprint</mat-icon
            >
            <label
              class="block font-semibold text-gray-700 dark:text-gray-300 text-sm uppercase tracking-wide"
            >
              Organization ID
            </label>
          </div>
          <p class="text-lg font-medium text-gray-900 dark:text-white pl-9">
            {{ adminData?.Name || "--" }}
          </p>
        </div>

        <div
          class="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center gap-3 mb-2">
            <mat-icon class="text-gray-500 dark:text-gray-400 text-lg"
              >business</mat-icon
            >
            <label
              class="block font-semibold text-gray-700 dark:text-gray-300 text-sm uppercase tracking-wide"
            >
              Organization
            </label>
          </div>
          <p class="text-lg font-medium text-gray-900 dark:text-white pl-9">
            {{ adminData?.OrganizationName || "--" }}
          </p>
        </div>

        <div
          class="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center gap-3 mb-2">
            <mat-icon class="text-gray-500 dark:text-gray-400 text-lg"
              >phone</mat-icon
            >
            <label
              class="block font-semibold text-gray-700 dark:text-gray-300 text-sm uppercase tracking-wide"
            >
              Phone
            </label>
          </div>
          <p class="text-lg font-medium text-gray-900 dark:text-white pl-9">
            {{ adminData?.Phone || "--" }}
          </p>
        </div>

        <div
          class="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-200 dark:border-gray-600 sm:col-span-2 lg:col-span-3"
        >
          <div class="flex items-center gap-3 mb-2">
            <mat-icon class="text-gray-500 dark:text-gray-400 text-lg"
              >location_on</mat-icon
            >
            <label
              class="block font-semibold text-gray-700 dark:text-gray-300 text-sm uppercase tracking-wide"
            >
              Address
            </label>
          </div>
          <p class="text-lg font-medium text-gray-900 dark:text-white pl-9">
            {{ adminData?.Address || "--" }}
          </p>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div class="loaderMain" *ngIf="pageLoader">
      <div class="flex flex-col items-center justify-center mt-10">
        <img
          src="assets/TrailFusion/Trail-Fusion-logo.jpg"
          class="LoaderImage w-24 h-24 rounded-xl"
        />
        <p
          class="loaderText mt-4 text-center text-gray-600 dark:text-gray-400 text-lg"
        >
          Loading organization details...
        </p>
      </div>
    </div>
  </div>
</div>
